const MYSHOPIFY_SUFFIX = '.myshopify.com';
// One DNS label: 1-63 chars, no leading or trailing hyphen (AS-11464).
// Keep in sync with assisty-bot src/helpers/shopify_admin_link.js,
// atlas frontend/src/lib/shopifyUrls.ts and hades src/utils/appLinks.ts.
const SHOP_HANDLE_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * Normalise `acme.myshopify.com` (any case) to the store handle Shopify admin
 * uses in its URLs. Returns null when the shop is missing or not a valid handle.
 */
export function toShopHandle(shop: string | null | undefined): string | null {
  if (!shop) return null;
  let handle = shop.trim().toLowerCase();
  if (handle.endsWith(MYSHOPIFY_SUFFIX)) handle = handle.slice(0, -MYSHOPIFY_SUFFIX.length);
  return SHOP_HANDLE_PATTERN.test(handle) ? handle : null;
}

/**
 * Build the canonical Shopify admin URL for the current in-app route
 * so it can be shared with teammates. The recipient lands in the same
 * embedded page inside Shopify admin.
 *
 * With no stored shop, or one that is not a valid store handle, the admin URL
 * would be `/store//apps/...` or `/store/acme-/apps/...` — a Shopify error
 * page — so fall back to the VUE_APP_HOST link (AS-11464).
 */
export function buildShopifyShareLink(fullPath: string): string {
  const handle = toShopHandle(localStorage.getItem('shopify_store'));
  if (!handle) return `${process.env.VUE_APP_HOST}${fullPath}`;
  const appHandle = process.env.VUE_APP_NAME || 'assisty';
  return `https://admin.shopify.com/store/${handle}/apps/${appHandle}${fullPath}`;
}

/**
 * Share link for an in-app route (AS-11463). Inside a shop it is the Shopify
 * admin URL, so the recipient opens the route embedded in that shop's admin:
 * a bare VUE_APP_HOST link carries no shop and lands a fresh tab on the
 * warbler login. Without a usable stored shop it is the VUE_APP_HOST link.
 */
export function buildReportShareLink(fullPath: string): string {
  return buildShopifyShareLink(fullPath);
}

export async function copyShopifyShareLink(fullPath: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(buildShopifyShareLink(fullPath));
    return true;
  } catch {
    return false;
  }
}
